import { Dispatch, FC, SetStateAction, startTransition, Suspense, useEffect, useState } from 'react';
import { Info, Pencil } from 'lucide-react';
import { Dropdown, DropdownChangeEvent, DropdownProps } from 'primereact/dropdown';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { DashboardAllUnitsQuery$data } from '../../graphQLOperations/__generated__/DashboardAllUnitsQuery.graphql';
import { sortByKey } from '../../businessLogicService/sortCriteria';
import { ExitSlipModalType, setExitSlipModal } from '../../redux/reducers/exitSlipModalSlice';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../redux/hooks';
import {
  exitSlipInitialState,
  selectedExitSlipFilter,
  setExitSlipFilter,
} from '../../redux/reducers/filterExitSlipSlice';

export interface availablesOptions {
  roadmap: DropdownItemModel[];
  unit: DropdownItemModel[];
  classes: DropdownItemModel[];
}
export interface DropdownItemModel {
  value: string;
  label: string;
}
interface Props {
  allUnits: DashboardAllUnitsQuery$data;
  availablesOptions: availablesOptions;
  setAvailablesOptions: Dispatch<SetStateAction<availablesOptions>>;
  modalType: ExitSlipModalType | undefined;
  applyRefetchSnapshot: boolean;
}

export const initAvailablesOptions = {
  roadmap: [],
  unit: [],
  classes: [],
};

//min-w-0 is what keeps a long class name in its own column: flex items default to min-width:auto
//and refuse to shrink under their content, so without it the Class(es) field widens and pushes
//Roadmap and Units out of the row
const fieldWrapperClass = 'tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-1';
const labelClass =
  'tw-flex tw-items-center tw-gap-2.5 tw-pl-3 tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-neutral-700';

//the shared shell of every control: 44px, 2px outline, 12px radius. PrimeReact renders the value
//inside the control and names that part per component, so :is() is what lets one list cover both
//— a bare comma in an arbitrary variant compiles to nothing
const controlClass = [
  '!tw-h-11 !tw-w-full !tw-items-center !tw-gap-3 !tw-rounded-xl !tw-border-2 !tw-border-solid !tw-border-neutral-100 !tw-bg-white !tw-px-3 !tw-shadow-none',
  '[&_:is(.p-dropdown-label,.p-multiselect-label)]:!tw-p-0',
  '[&_:is(.p-dropdown-label,.p-multiselect-label)]:tw-font-atkinson',
  '[&_:is(.p-dropdown-label,.p-multiselect-label)]:!tw-text-base',
  '[&_:is(.p-dropdown-label,.p-multiselect-label)]:!tw-font-bold',
  '[&_:is(.p-dropdown-label,.p-multiselect-label)]:!tw-leading-5',
  '[&_:is(.p-dropdown-label,.p-multiselect-label)]:!tw-text-neutral-950',
  '[&_:is(.p-dropdown-label,.p-multiselect-label).p-placeholder]:!tw-font-normal',
  '[&_:is(.p-dropdown-label,.p-multiselect-label).p-placeholder]:!tw-leading-6',
  '[&_:is(.p-dropdown-label,.p-multiselect-label).p-placeholder]:!tw-text-neutral-700',
  '[&_:is(.p-dropdown-trigger,.p-multiselect-trigger)]:!tw-w-[18px]',
  '[&_:is(.p-dropdown-trigger,.p-multiselect-trigger)]:!tw-text-neutral-950',
].join(' ');

//the list renders in a portal, so panelClassName is the only way in. _extensions.scss styles
//.p-dropdown-panel and never grew a .p-multiselect-panel twin, which is why the two lists
//diverged; these are that block's values, applied to both
const panelClass = [
  '!tw-rounded-[0.625rem] !tw-border-none !tw-shadow-[0_1.25rem_3rem_rgba(57,57,57,0.6)]',
  '[&_:is(.p-dropdown-items,.p-multiselect-items)]:!tw-p-0',
  '[&_:is(.p-dropdown-item,.p-multiselect-item)]:tw-font-atkinson',
  '[&_:is(.p-dropdown-item,.p-multiselect-item)]:!tw-px-4',
  '[&_:is(.p-dropdown-item,.p-multiselect-item)]:!tw-py-2',
  '[&_:is(.p-dropdown-item,.p-multiselect-item):first-child]:!tw-rounded-t-[0.625rem]',
  '[&_:is(.p-dropdown-item,.p-multiselect-item):last-child]:!tw-rounded-b-[0.625rem]',
  '[&_:is(.p-dropdown-item,.p-multiselect-item):not(.p-highlight):not(.p-disabled):hover]:!tw-bg-[#17275D]',
  '[&_:is(.p-dropdown-item,.p-multiselect-item):not(.p-highlight):not(.p-disabled):hover]:!tw-text-white',
].join(' ');

//one chip, two renderers: the MultiSelect builds its own tokens, the EDIT Dropdown borrows the
//look through valueTemplate. pt concatenates className rather than replacing it, so the
//PrimeReact base classes survive
const chipClass =
  '!tw-m-0 !tw-inline-flex !tw-items-center !tw-gap-1 !tw-rounded-md !tw-border !tw-border-b-2 !tw-border-solid !tw-border-stone-200 !tw-bg-stone-50 !tw-px-2 !tw-py-0.5 tw-font-atkinson !tw-text-xs !tw-font-bold !tw-leading-4 !tw-text-stone-800';

const VISIBLE_CLASS_CHIPS = 1;

const ExitSlipModalFilters: FC<Props> = ({
  allUnits,
  availablesOptions,
  setAvailablesOptions,
  modalType,
  applyRefetchSnapshot,
}) => {
  const dispatch = useDispatch();
  const exitSlipFilterRedux = useAppSelector(selectedExitSlipFilter);
  const [tipDismissed, setTipDismissed] = useState(false);

  useEffect(() => {
    if (exitSlipFilterRedux.roadmap) {
      const unitsByTargetGroup = allUnits.allTargets
        .filter(el => el.targetGroup?.id === (exitSlipFilterRedux.roadmap as string))
        ?.map(element => ({
          value: element.id,
          label: element.name + ' - ' + element.summary,
        }))
        .sort(sortByKey('label'));
      setAvailablesOptions(prev => ({ ...prev, unit: unitsByTargetGroup }));
    }
  }, [exitSlipFilterRedux.roadmap]);

  const showTip = modalType === ExitSlipModalType.ASSIGN && !tipDismissed && exitSlipFilterRedux.classes.length > 0;
  const hiddenClassCount = exitSlipFilterRedux.classes.length - VISIBLE_CLASS_CHIPS;

  return (
    <>
      <div className="flex justify-content-between tw-p-6 align-items-center border-bottom-1 surface-border">
        <span
          className={`pi ${
            modalType === ExitSlipModalType.ASSIGN
              ? 'pi-check-circle'
              : 'tw-bg-stone-50 tw-size-11 tw-rounded-lg tw-inline-flex tw-justify-center tw-items-center'
          }  mr-2 font-bold text-xl`}
        >
          {modalType === ExitSlipModalType.EDIT && <Pencil />}
        </span>
        <h3 className="w-full font-bold">
          {modalType === ExitSlipModalType.ASSIGN
            ? 'Assign exit tickets for your class(es)'
            : 'Manage assigned tickets'}
        </h3>
        <i
          className="nc_icon-nc_close icon-sm cursor-pointer"
          onClick={() => {
            dispatch(
              setExitSlipModal({ visible: false, exitSlipKey: applyRefetchSnapshot ? new Date().getTime() : 0 }),
            );
            dispatch(setExitSlipFilter(exitSlipInitialState.value));
          }}
        />
      </div>
      <Suspense>
        <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-b-2 tw-border-solid tw-border-neutral-100 tw-bg-white tw-px-6 tw-py-5">
          <div className="tw-flex tw-w-full tw-items-center tw-gap-5">
            <div className={fieldWrapperClass}>
              <label htmlFor="classes" className={labelClass}>
                {`Class${modalType === ExitSlipModalType.ASSIGN ? '(es)' : ''}`}
                <span className="tw-text-[#B82828]">*</span>
              </label>
              {modalType === ExitSlipModalType.EDIT ? (
                <Dropdown
                  value={exitSlipFilterRedux.classes[0] ?? ''}
                  onChange={(e: DropdownChangeEvent) => {
                    startTransition(() => {
                      dispatch(setExitSlipFilter({ ...exitSlipFilterRedux, classes: [e.value] }));
                    });
                  }}
                  options={availablesOptions.classes}
                  optionLabel="label"
                  inputId="classes"
                  placeholder="Select a Class"
                  valueTemplate={(option: DropdownItemModel | null, props: DropdownProps) =>
                    option ? <span className={chipClass}>{option.label}</span> : <>{props.placeholder}</>
                  }
                  className={controlClass}
                  panelClassName={panelClass}
                />
              ) : (
                <MultiSelect
                  selectionLimit={availablesOptions.classes.length}
                  value={exitSlipFilterRedux.classes}
                  onChange={(e: MultiSelectChangeEvent) => {
                    startTransition(() => {
                      dispatch(setExitSlipFilter({ ...exitSlipFilterRedux, classes: e.value }));
                    });
                  }}
                  options={availablesOptions.classes}
                  optionLabel="label"
                  //display has to flip, not just maxSelectedLabels: the chip branch of
                  //getLabelContent slices to maxSelectedLabels and returns tokens, so it never
                  //reaches the selectedItemsLabel summary that getLabel() produces
                  display={hiddenClassCount > 0 ? 'comma' : 'chip'}
                  maxSelectedLabels={VISIBLE_CLASS_CHIPS}
                  selectedItemsLabel="{0} classes selected"
                  inputId="classes"
                  placeholder="Select a Class(es)"
                  pt={{
                    label: {
                      className: '!tw-flex !tw-min-w-0 !tw-flex-nowrap !tw-items-center !tw-gap-1 !tw-overflow-hidden',
                    },
                    token: { className: `${chipClass} !tw-min-w-0` },
                    tokenLabel: { className: '!tw-min-w-0 !tw-truncate' },
                    removeTokenIcon: { className: '!tw-size-3 !tw-shrink-0' },
                  }}
                  className={controlClass}
                  panelClassName={panelClass}
                />
              )}
            </div>

            <div className={fieldWrapperClass}>
              <label htmlFor="roadmap" className={labelClass}>
                Roadmap (Grade)
                <span className="tw-text-[#B82828]">*</span>
              </label>
              <Dropdown
                value={exitSlipFilterRedux.roadmap}
                onChange={(e: DropdownChangeEvent) => {
                  dispatch(setExitSlipFilter({ ...exitSlipFilterRedux, roadmap: e.value, unit: '' }));
                }}
                options={availablesOptions.roadmap}
                optionLabel="label"
                inputId="roadmap"
                placeholder="Select a Roadmap"
                className={controlClass}
                panelClassName={panelClass}
              />
            </div>

            <div className={fieldWrapperClass}>
              <label htmlFor="units" className={labelClass}>
                Units
                <span className="tw-text-xs tw-font-normal tw-leading-4 tw-text-[767676]">optional</span>
              </label>
              <Dropdown
                value={exitSlipFilterRedux.unit}
                onChange={(e: DropdownChangeEvent) =>
                  dispatch(setExitSlipFilter({ ...exitSlipFilterRedux, unit: e.value ?? '' }))
                }
                options={availablesOptions.unit}
                optionLabel="label"
                inputId="units"
                placeholder="Select a Unit"
                showClear
                disabled={exitSlipFilterRedux.roadmap === ''}
                className={controlClass}
                panelClassName={panelClass}
              />
            </div>
          </div>

          {showTip && (
            <div className="tw-flex tw-w-fit tw-items-center tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-100 tw-bg-neutral-50 tw-px-3 tw-py-1.5">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <Info size={16} strokeWidth={2} className="tw-shrink-0 tw-text-neutral-950" />
                <p className="tw-m-0 tw-font-atkinson tw-text-xs tw-leading-4 tw-text-neutral-950">
                  <span className="tw-font-bold">Tip:</span> All selected classes get the same date. To use different
                  dates, deselect classes first
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss tip"
                onClick={() => setTipDismissed(true)}
                className="tw-flex tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-neutral-950"
              >
                <i className="nc_icon-nc_close icon-sm" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </Suspense>
    </>
  );
};

export default ExitSlipModalFilters;
